import { atom, useRecoilValue } from 'recoil'
import { selectorWithoutCache as selector } from 'util/RecoilWithoutCache'
import {
    OrderActionEnum,
    OrderPriceTypeEnum,
    OrderTradeDirectionEnum,
    OrderTradeWay,
    OrderTriggerTypeEnum,
    OrderTypeEnum
} from 'constants/order'
import {
    MixContractDelegateType,
    MixContractHoldSide,
    MixContractMarginMode,
    MixContractOrderType,
    MixContractTimeInForceEnum,
    OrderPlacerParam
} from 'constants/MixContractGlobal'
import { CurrentBusinessLineState } from 'store/data/Base'
import { MixContractInfoState, getMixContractInfoBySymbolId } from 'store/data/Contract'
import { CurrentMarginSelector } from 'store/data/Margin'
import {
    FinalTPSLOpenPriceSelector,
    LimitQuickRealAmountSelector,
    QuickRealAmountSelector,
    RealAmountSelector
} from './selector'
import { BusinessLineEnum, BusinessSource, EnterPointSource } from 'constants/base'
import { getPriceStep, isValidInput } from 'helper/checkers'
import { useMyIntl } from 'hooks'
import { MixContractPositionPushInfo } from 'store/entity'
import { UserPreferenceSettingState } from 'store/data/User'
import { PositionModeEnum, TradeUnitEnum } from 'constants/vars'
import LocalCache, { CacheKey } from 'util/LocalCache'
import { MixContractUserAccountModeSelector } from 'store/data/Account'
import message from 'components/common/BitMessage'
import { TPSLTYPE } from 'constants/common'
import {
    calPreStopLossPrice,
    calPreTakeProfitPrice,
    preTPSLLogHelper
} from 'helper/formula/MixContractTpsl'
import { getTokenPriceByTokenId } from 'store/data/Token'
import {
    BBOOrderState,
    BBOOrderPriceIndexState,
    BBODepthAsksAndBidsBuySelector,
    BBODepthAsksAndBidsSellSelector
} from 'store/data/BBO'
import { BBO_LIMIT_OFFSET } from 'constants/bbo'

/**
 * 将委托的各种属性直接抽象到state层面
 */

/**
 * 开平仓选项
 */
export const OPActionState = atom<OrderActionEnum>({
    key: 'op-action',
    default: OrderActionEnum.OPEN
})

/**
 * isOpen
 */
export const OPActionSelector = selector<boolean>({
    key: 'op-action-selector',
    get: ({ get }) => {
        return get(OPActionState) === OrderActionEnum.OPEN
    }
})

/**
 * 普通委托，计划委托
 */
export const OPTypeState = atom<OrderTypeEnum>({
    key: 'op-order-type',
    default: LocalCache.get(CacheKey.OP_TYPE_STATE) || OrderTypeEnum.NORMAL
})

/**
 * 限价、市价
 */
export const OPPriceTypeState = atom<OrderPriceTypeEnum>({
    key: 'op-price-type',
    default: LocalCache.get(CacheKey.OP_ORDER_PRICE_TYPE) || OrderPriceTypeEnum.LIMIT
})

/**
 * 计划委托--触发类型
 */
export const OPTriggerTypeState = atom<OrderTriggerTypeEnum>({
    key: 'op-trigger-type',
    default: OrderTriggerTypeEnum.MARKET
})

/**
 * 普通止盈--触发类型
 */
export const OPSurplusTypeState = atom<OrderTriggerTypeEnum>({
    key: 'op-trigger-opsurplus-type',
    default: OrderTriggerTypeEnum.MARKET
})

/**
 * 触发价格
 */
export const OPTriggerPriceState = atom<string>({
    key: 'op-trigger-price',
    default: ''
})

/**
 * 执行价格
 */
export const OPExecutePriceState = atom<string>({
    key: 'op-exe-price',
    default: ''
})

/**
 * 价格
 */
export const OPPriceState = atom<string>({
    key: 'op-price',
    default: ''
})

/**
 * 数量
 */
export const OPAmountState = atom<string>({
    key: 'op-amount',
    default: ''
})

/**
 * 快速下单的数量
 */
export const QOAmountState = atom<string>({
    key: 'qo-amount',
    default: ''
})

/**
 * 快速下单的百分比
 */
export const QOPercentageState = atom<number>({
    key: 'qo-percentage',
    default: 0
})

/**
 * 限价快速下单-价格
 */
export const LimitOPPriceState = atom<string>({
    key: 'limit-op-price',
    default: ''
})

/**
 * 限价快速下单-数量
 */
export const LimitOPAmountState = atom<string>({
    key: 'limit-op-amount',
    default: ''
})

/**
 * 限价快速下单的百分比
 */
export const LimitQOPercentageState = atom<number>({
    key: 'limit-qo-percentage',
    default: 0
})

/**
 * 百分比
 */
export const OPPercentageState = atom<number>({
    key: 'op-percentage',
    default: 0
})

/**
 * 买入、卖出--被称作交易方向
 */
export const OPTradeDirectionState = atom<OrderTradeDirectionEnum>({
    key: 'op-trade-direction',
    default: OrderTradeDirectionEnum.Buy
})

/**
 * 时效选项
 */
export const OPTimeInForceState = atom<MixContractTimeInForceEnum>({
    key: 'op-tif',
    default: LocalCache.get(CacheKey.OP_TIME_INFORCES_TATE) || MixContractTimeInForceEnum.GTC
})

/**
 * 是否使用预设止盈止损
 */
const OPUsePreset = atom<boolean>({
    key: 'op-use-preset',
    default: false
})

export const OPUsePresetState = selector<boolean>({
    key: 'op-use-preset-selector',
    get: ({ get }) => {
        return get(OPUsePreset)
    },
    set: ({ set }, value) => {
        set(OPUsePreset, value)
        // 不使用预设止盈止损时，直接清空相关价格
        if (!value) {
            set(OPPresetTakeProfitPriceState, '')
            set(OPPresetStopLossPriceState, '')
            set(OPExecTakeProfitPriceState, '')
            set(OPExecStopLossPriceState, '')
            set(OPPresetTakeProfitExecPriceTypeState, OrderPriceTypeEnum.MARKET)
            set(OPPresetStopLossExecPriceTypeState, OrderPriceTypeEnum.MARKET)
        }
    }
})

/** 修改为只有高级使用
 * 是否使用开多止盈止损
 */
export const OPUseLongPreset = atom<boolean>({
    key: 'op-use-long-preset',
    default: true
})

/**
 * 是否  只减仓  单向持仓模式下 展示
 */
const OPOnlyReducePositionPreset = atom<boolean>({
    key: 'op-only-reduce-position-preset',
    default: false
})

export const OPOnlyReducePositionPresetState = selector<boolean>({
    key: 'op-only-reduce-position-preset-selector',
    get: ({ get }) => {
        return get(OPOnlyReducePositionPreset)
    },
    set: ({ set }, value) => {
        set(OPOnlyReducePositionPreset, value)
    }
})

/**
 * 预设止盈价
 *
 */
export const OPPresetTakeProfitPriceState = atom<string>({
    key: 'op-tp-price',
    default: ''
})

/**
 * 预设止损价
 */
export const OPPresetStopLossPriceState = atom<string>({
    key: 'op-sl-price',
    default: ''
})

/**
 * 执行止盈价
 * 预设止盈价会触发执行止盈
 * 没有执行价格默认按市价执行
 * 存在执行价格 会挂一个限价单
 */
export const OPExecTakeProfitPriceState = atom<string>({
    key: 'op-exec-tp-price',
    default: ''
})

/**
 * 执行止损价
 * 没有执行价格默认按市价执行
 * 存在执行价格 会挂一个限价单
 */
export const OPExecStopLossPriceState = atom<string>({
    key: 'op-exec-sl-price',
    default: ''
})

/**
 * 止盈触发类型
 * */
export const OPPresetTriggerTakeProfitTypeState = atom<OrderTriggerTypeEnum>({
    key: 'op-trigger-tp-type',
    default: OrderTriggerTypeEnum.MARKET
})

/**
 * 止损触发类型
 * */
export const OPPresetTriggerStopLossTypeState = atom<OrderTriggerTypeEnum>({
    key: 'op-trigger-sl-type',
    default: OrderTriggerTypeEnum.MARKET
})

// 止盈委托价格类型
export const OPPresetTakeProfitExecPriceTypeState = atom<OrderPriceTypeEnum>({
    key: 'op-preset-tp-exec-price-type',
    default: OrderPriceTypeEnum.MARKET
})

// 止损委托价格类型
export const OPPresetStopLossExecPriceTypeState = atom<OrderPriceTypeEnum>({
    key: 'op-preset-sl-exec-price-type',
    default: OrderPriceTypeEnum.MARKET
})

/**
 * 预设止盈的类型 止盈率 涨跌幅 盈亏
 *
 */
export const OPPresetTakeProfitTypeState = atom<TPSLTYPE>({
    key: 'op-preset-take-profit-type-state',
    default: TPSLTYPE.TYPE_A_RATE_RETURN
})

/**
 * 预设止盈 止盈率 涨跌幅 盈亏  对应的值
 *
 */
export const OPPresetTakeProfitValueState = atom<string>({
    key: 'op-preset-take-profit-value-state',
    default: ''
})

/**
 * 预设止损的类型 止盈率 涨跌幅 盈亏
 *
 */
export const OPPresetStopLossTypeState = atom<TPSLTYPE>({
    key: 'op-preset-stop-loss-type-state',
    default: TPSLTYPE.TYPE_A_RATE_RETURN
})

/**
 * 预设止损 止盈率 涨跌幅 盈亏  对应的值
 *
 */
export const OPPresetStopLossValueState = atom<string>({
    key: 'op-preset-stop-loss-value-state',
    default: ''
})

/**
 *
 *
 */
export const OPPresetTakeProfitErrorState = atom<boolean>({
    key: 'op-preset-take-profit-error-state',
    default: false
})

/**
 *
 *
 */
export const OPPresetStopLossErrorState = atom<boolean>({
    key: 'op-preset-stop-loss-error-state',
    default: false
})

//////////*********** 高级预设止盈止损使用 搞成全局是为了埋点 其他地方不要引用  begin */
/**
 * 高级预设止盈的类型 搞成全局是为了埋点 止盈率 涨跌幅 盈亏
 *
 */
export const OPAdvancePresetTakeProfitTypeState = atom<TPSLTYPE>({
    key: 'op-advance-preset-take-profit-type-state',
    default: TPSLTYPE.TYPE_A_RATE_RETURN
})

/**
 * 高级预设止损的类型
 *
 */
export const OPAdvancePresetStopLossTypeState = atom<TPSLTYPE>({
    key: 'op-advance-preset-stop-loss-type-state',
    default: TPSLTYPE.TYPE_A_RATE_RETURN
})

/**
 * 高级预设止盈的类型 搞成全局是为了确认是否使用了高级止盈止损
 *
 */
export const OPAdvancePresetTakeProfitTypeValue = atom<string>({
    key: 'op-advance-preset-take-profit-type-value',
    default: ''
})

/**
 * 高级预设止损的类型
 *
 */
export const OPAdvancePresetStopLossTypeValue = atom<string>({
    key: 'op-advance-preset-stop-loss-type-value',
    default: ''
})

/**
 * 负责转换一下
 * @param type
 * @param priceType
 */
function getRealOrderType(
    type: OrderTypeEnum,
    priceType: OrderPriceTypeEnum
): MixContractOrderType {
    if (type === OrderTypeEnum.NORMAL) {
        return priceType === OrderPriceTypeEnum.LIMIT
            ? MixContractOrderType.LIMIT
            : MixContractOrderType.MARKET
    } else if (type === OrderTypeEnum.PLAN) {
        return MixContractOrderType.PLAN
    } else {
        // 分段订单返回的也是 MixContractOrderType.LIMIT
        return MixContractOrderType.LIMIT
    }
}

/**
 * 转换委托类型
 * @param action
 * @param direction
 */
export function getRealDelegateType(
    action: OrderActionEnum,
    direction: OrderTradeDirectionEnum
): MixContractDelegateType {
    return direction === OrderTradeDirectionEnum.Buy
        ? action === OrderActionEnum.OPEN
            ? MixContractDelegateType.OPEN_LONG
            : MixContractDelegateType.CLOSE_SHORT
        : action === OrderActionEnum.OPEN
          ? MixContractDelegateType.OPEN_SHORT
          : MixContractDelegateType.CLOSE_LONG
}

export interface TempOrderPlacerParam {
    businessLine: number
    symbolId: string
    symbolType: number
    direction: OrderTradeDirectionEnum
    action: OrderActionEnum
    type: OrderTypeEnum
    priceType: OrderPriceTypeEnum
    delegateCount: string
    price: string
    triggerPrice: string
    triggerType: OrderTriggerTypeEnum
    targetPrice: string
    tokenId: string
    isReducePosition?: boolean
    onlyLow?: boolean
    leverage: number
    marginMode: MixContractMarginMode
    tif: MixContractTimeInForceEnum
    inputAmount: string
    inputAmountPercent: number
    tradeUnit: TradeUnitEnum
    presetTakeProfitPrice: string
    presetStopLossPrice: string
    averageOpenPrice?: string
    deposit?: string
    bestClosePrice?: string | number
    bestClosePriceReverse?: string | number
    orderType?: number
    TpslLabel?: string
}

export interface TempOrderPlacerParamGetter {
    (direction: OrderTradeDirectionEnum): TempOrderPlacerParam
}

export interface RealOrderPlacerParamGetter {
    (direction: OrderTradeDirectionEnum): Partial<OrderPlacerParam>
}
export interface CommonRealOrderPlacerParamGetter {
    ({
        direction,
        delegateCount,
        delegatePrice
    }: {
        direction: OrderTradeDirectionEnum
        delegateCount: string
        delegatePrice: string
    }): Partial<OrderPlacerParam>
}

export interface QuickOrderParamGetter {
    (direction: OrderTradeDirectionEnum): Partial<OrderPlacerParam>
}

// 限价快速下单参数组装器
export interface LimitQuickOrderParamGetter {
    (direction: OrderTradeDirectionEnum): Partial<OrderPlacerParam>
}

/**
 * 返回一个参数组装器
 */
export function useOrderPlacerParamGetter(): [
    TempOrderPlacerParamGetter,
    RealOrderPlacerParamGetter,
    CommonRealOrderPlacerParamGetter
] {
    const t = useMyIntl()
    const positionModeSet = useRecoilValue(UserPreferenceSettingState).positionModeSet // 单双向持仓模式
    const isReducePosition = useRecoilValue(OPOnlyReducePositionPresetState) // 只减仓

    const businessLine = useRecoilValue(CurrentBusinessLineState)
    const contractConfig = useRecoilValue(MixContractInfoState)
    const tokenId = useRecoilValue(CurrentMarginSelector)

    const action = useRecoilValue(OPActionState)
    const type = useRecoilValue(OPTypeState)
    const priceType = useRecoilValue(OPPriceTypeState)
    const delegateCountForBuy = useRecoilValue(RealAmountSelector(OrderTradeDirectionEnum.Buy))
    const delegateCountForSell = useRecoilValue(RealAmountSelector(OrderTradeDirectionEnum.Sell))
    const tif = useRecoilValue(OPTimeInForceState)

    const price = useRecoilValue(OPPriceState)
    const triggerPrice = useRecoilValue(OPTriggerPriceState)
    const targetPrice = useRecoilValue(OPExecutePriceState)
    const triggerType = useRecoilValue(OPTriggerTypeState)
    const surplusTriggerType = useRecoilValue(OPSurplusTypeState)

    const usePreset = useRecoilValue(OPUsePresetState)
    const presetTakeProfitPrice = useRecoilValue(OPPresetTakeProfitPriceState)
    const presetStopLossPrice = useRecoilValue(OPPresetStopLossPriceState)

    const exeTakeProfitPrice = useRecoilValue(OPExecTakeProfitPriceState)
    const exeStopLossPrice = useRecoilValue(OPExecStopLossPriceState)

    const userPreferenceSetting = useRecoilValue(UserPreferenceSettingState)

    const { marginMode, fixedShortLeverage, fixedLongLeverage, crossMarginLeverage } =
        useRecoilValue(MixContractUserAccountModeSelector)
    const inputAmount = useRecoilValue(OPAmountState)
    const inputAmountPercent = useRecoilValue(OPPercentageState)
    const tradeUnit = useRecoilValue(UserPreferenceSettingState).contractUnitSet

    // 新增止盈止损的下单类型
    const finalOpenPrice = useRecoilValue(FinalTPSLOpenPriceSelector)
    const presetTakeProfitType = useRecoilValue(OPPresetTakeProfitTypeState)
    const presetTakeProfitValue = useRecoilValue(OPPresetTakeProfitValueState)
    const presetStopLossType = useRecoilValue(OPPresetStopLossTypeState)
    const presetStopLossValue = useRecoilValue(OPPresetStopLossValueState)
    //高级止盈止损的止盈止损选择类型
    const advancePresetTakeProfitType = useRecoilValue(OPAdvancePresetTakeProfitTypeState)
    const advancePresetStopLossType = useRecoilValue(OPAdvancePresetStopLossTypeState)
    //高级止盈止损的止盈止损选择类型 end

    const presetTppTriType = useRecoilValue(OPPresetTriggerTakeProfitTypeState)
    const presetSlpTriType = useRecoilValue(OPPresetTriggerStopLossTypeState)
    const { placeConfig, baseSymbol } = useRecoilValue(
        getMixContractInfoBySymbolId(contractConfig.symbolId)
    )
    // 保证金汇价
    const tokenMarkPrice = useRecoilValue(getTokenPriceByTokenId(tokenId)).indexPrice
    //限价bbo
    const bboOrderState = useRecoilValue(BBOOrderState)
    const bboOrderPriceIndexState = useRecoilValue(BBOOrderPriceIndexState)
    // bbo的价格
    const bboBuyPrice = useRecoilValue(BBODepthAsksAndBidsBuySelector)
    // bbo的价格
    const bboSellPrice = useRecoilValue(BBODepthAsksAndBidsSellSelector)
    // 币本位合约保证金与交易左币一致-用于止盈止损盈亏类型tips止盈价计算
    const isCoinMarginContract =
        (businessLine === BusinessLineEnum.USD_MIX_CONTRACT_BL ||
            businessLine === BusinessLineEnum.S_USD_MIX_CONTRACT_BL) &&
        baseSymbol === tokenId
    function getTempOrderPlacerParam(direction: OrderTradeDirectionEnum) {
        return {
            businessLine,
            isReducePosition,
            onlyLow: isReducePosition,
            symbolId: contractConfig.symbolId,
            symbolType: contractConfig.symbolType,
            direction,
            action,
            type,
            priceType,
            delegateCount:
                direction === OrderTradeDirectionEnum.Buy
                    ? delegateCountForBuy
                    : delegateCountForSell,
            price: bboOrderState
                ? direction === OrderTradeDirectionEnum.Sell
                    ? bboBuyPrice
                    : bboSellPrice
                : price,
            triggerPrice,
            triggerType,
            targetPrice,
            tokenId,
            surplusTriggerType,
            leverage:
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
            marginMode: marginMode,
            tif,
            orderType: getRealOrderType(type, priceType),
            inputAmount,
            inputAmountPercent,
            tradeUnit,
            presetTakeProfitPrice: calPreTakeProfitPrice(
                usePreset,
                presetTakeProfitPrice,
                direction,
                presetTakeProfitType,
                presetTakeProfitValue,
                '' + finalOpenPrice,
                delegateCountForBuy,
                // marginMode === MixContractMarginMode.FIXED_MARGIN
                //     ? fixedLongLeverage
                //     : crossMarginLeverage,
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
                placeConfig.delegatePricePlace,
                tokenMarkPrice,
                placeConfig.priceEndStep,
                isCoinMarginContract,
                true
            ),
            presetStopLossPrice: calPreStopLossPrice(
                usePreset,
                presetStopLossPrice,
                direction,
                presetStopLossType,
                presetStopLossValue,
                '' + finalOpenPrice,
                delegateCountForSell,
                // marginMode === MixContractMarginMode.FIXED_MARGIN
                //     ? fixedShortLeverage
                //     : crossMarginLeverage,
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
                placeConfig.delegatePricePlace,
                tokenMarkPrice,
                placeConfig.priceEndStep,
                isCoinMarginContract,
                true
            ),
            presetTppTriType,
            presetSlpTriType,
            exeTakeProfitPrice,
            exeStopLossPrice,
            TpslLabel: preTPSLLogHelper(
                presetTakeProfitPrice,
                presetStopLossPrice,
                presetTakeProfitType,
                presetTakeProfitValue,
                presetStopLossType,
                presetStopLossValue,
                advancePresetTakeProfitType,
                advancePresetStopLossType
            )
        }
    }

    // 目前仅供分段订单使用
    const commonRealOrderPlacerParamGetter = ({ direction, delegateCount, delegatePrice }) => {
        let orderType = getRealOrderType(type, priceType)

        let result: Partial<OrderPlacerParam> = {
            tokenId,
            symbolId: contractConfig.symbolId,
            businessLine,
            secondBusinessLine: 'N/A',
            businessSource:
                orderType === MixContractOrderType.LIMIT
                    ? BusinessSource.NORMAL
                    : BusinessSource.MARKET,
            enterPointSource: EnterPointSource.WEB,
            delegateCount,
            orderType,
            delegateType: getRealDelegateType(action, direction),
            pricedSymbol: contractConfig.pricedSymbol,
            delegatePrice
        }

        if (positionModeSet === PositionModeEnum.ONE_WAY) {
            // 需要把是否包含已委托传入
            result.cancelOrder = !!userPreferenceSetting.quantityIncludesDelegated

            // 买入 21  卖出 22
            result.delegateType =
                direction === OrderTradeDirectionEnum.Buy
                    ? MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
                    : MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE
            result.onlyLow = result.isReducePosition = isReducePosition
        }

        return result
    }

    function realOrderPlacerParamGetter(direction: OrderTradeDirectionEnum) {
        let tempDelegateCount =
            direction === OrderTradeDirectionEnum.Buy ? delegateCountForBuy : delegateCountForSell
        if (!isValidInput(tempDelegateCount)) {
            message.error(t('error_text.margin_count_not_enough', '委托数量不足'))
            return
        }

        // 必填项校验都在OPForm中完成
        // 这个orderType是请求用的，是type和priceType聚合而成
        let orderType = getRealOrderType(type, priceType)

        let result: Partial<OrderPlacerParam> = {
            tokenId,
            symbolId: contractConfig.symbolId,
            businessLine,
            secondBusinessLine: 'N/A',
            businessSource:
                orderType === MixContractOrderType.LIMIT
                    ? BusinessSource.NORMAL
                    : BusinessSource.MARKET,
            enterPointSource: EnterPointSource.WEB,
            delegateCount: tempDelegateCount,
            orderType: priceType,
            delegateType: getRealDelegateType(action, direction),
            pricedSymbol: contractConfig.pricedSymbol
        }

        // 不同的类型附加不同的参数
        switch (orderType) {
            case MixContractOrderType.LIMIT: {
                result.timeInForceValue = tif
                result.delegatePrice = price
                break
            }
            case MixContractOrderType.MARKET: {
                // 闪电委托现在默认也需要一个timeInForce值
                result.timeInForceValue = MixContractTimeInForceEnum.GTC
                break
            }
            case MixContractOrderType.PLAN: {
                //计划委托 执行价格类型  1 限价 2 市价
                result.planType = 0
                result.triggerPrice = triggerPrice
                result.executePrice = priceType === OrderPriceTypeEnum.LIMIT ? targetPrice : ''
                result.triggerType = triggerType
                break
            }
        }
        if (orderType !== MixContractOrderType.PLAN && action === OrderActionEnum.CLOSE) {
            // 平仓需要附带平仓是否包含已委托
            result.cancelOrder = !!userPreferenceSetting.quantityIncludesDelegated
        }

        // 预设止盈止损相关
        if (usePreset && action === OrderActionEnum.OPEN) {
            let localPresetTakeProfitPrice = calPreTakeProfitPrice(
                usePreset,
                presetTakeProfitPrice,
                direction,
                presetTakeProfitType,
                presetTakeProfitValue,
                finalOpenPrice,
                delegateCountForBuy,
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? fixedLongLeverage
                    : crossMarginLeverage,
                placeConfig.delegatePricePlace,
                tokenMarkPrice,
                placeConfig.priceEndStep,
                isCoinMarginContract,
                true
            )
            let localPresetStopLossPrice = calPreStopLossPrice(
                usePreset,
                presetStopLossPrice,
                direction,
                presetStopLossType,
                presetStopLossValue,
                finalOpenPrice,
                delegateCountForSell,
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? fixedShortLeverage
                    : crossMarginLeverage,
                placeConfig.delegatePricePlace,
                tokenMarkPrice,
                placeConfig.priceEndStep,
                isCoinMarginContract,
                true
            )
            localPresetTakeProfitPrice &&
                (result.presetTakeProfitPrice = localPresetTakeProfitPrice)
            localPresetStopLossPrice && (result.presetStopLossPrice = localPresetStopLossPrice)
            // let a = bboTPSLPriceIndexType

            presetTakeProfitPrice && (result.presetTakeProfitPrice = presetTakeProfitPrice)
            presetStopLossPrice && (result.presetStopLossPrice = presetStopLossPrice)
            // 计划委托和正常下单字段稍有不同 业务上意义是一样的
            if (orderType !== MixContractOrderType.PLAN) {
                presetTppTriType && (result.presetTppTriType = presetTppTriType)
                presetSlpTriType && (result.presetSlpTriType = presetSlpTriType)
                exeTakeProfitPrice && (result.exeTakeProfitPrice = exeTakeProfitPrice)
                exeStopLossPrice && (result.exeStopLossPrice = exeStopLossPrice)
            } else {
                presetTppTriType && (result.presetTPTriggerType = presetTppTriType)
                presetSlpTriType && (result.presetSLTriggerType = presetSlpTriType)
                exeTakeProfitPrice && (result.presetTPExcutePrice = exeTakeProfitPrice)
                exeStopLossPrice && (result.presetSLExcutePrice = exeStopLossPrice)
            }
        }

        //新增bbo限价BBO 覆盖前面限价的初始赋值
        if (bboOrderState && bboOrderPriceIndexState > -1) {
            result.businessSource = BBO_LIMIT_OFFSET + bboOrderPriceIndexState
            //bbo限价 不能传价格
            result.delegatePrice = null
        }

        // 单向持仓相关逻辑
        if (positionModeSet === PositionModeEnum.ONE_WAY) {
            // 需要把是否包含已委托传入
            result.cancelOrder = !!userPreferenceSetting.quantityIncludesDelegated

            // 买入 21  卖出 22
            result.delegateType = direction === OrderTradeDirectionEnum.Buy ? 21 : 22
            result.onlyLow = result.isReducePosition = isReducePosition
        }

        return result
    }

    return [getTempOrderPlacerParam, realOrderPlacerParamGetter, commonRealOrderPlacerParamGetter]
}

export function useMarketQuickOrderPlacerParamGetter(): [
    TempOrderPlacerParamGetter,
    QuickOrderParamGetter
] {
    const positionModeSet = useRecoilValue(UserPreferenceSettingState).positionModeSet // 单双向持仓模式
    const userPreferenceSetting = useRecoilValue(UserPreferenceSettingState)
    const tradeUnit = useRecoilValue(UserPreferenceSettingState).contractUnitSet
    const isReducePosition = useRecoilValue(OPOnlyReducePositionPresetState) // 只减仓
    const businessLine = useRecoilValue(CurrentBusinessLineState)
    const contractConfig = useRecoilValue(MixContractInfoState)
    const tokenId = useRecoilValue(CurrentMarginSelector)

    const action = useRecoilValue(OPActionState)
    const type = useRecoilValue(OPTypeState)
    // const priceType = useRecoilValue(OPPriceTypeState)
    const t = useMyIntl()
    const price = useRecoilValue(OPPriceState)
    const triggerPrice = useRecoilValue(OPTriggerPriceState)
    const targetPrice = useRecoilValue(OPExecutePriceState)
    const triggerType = useRecoilValue(OPTriggerTypeState)
    const surplusTriggerType = useRecoilValue(OPSurplusTypeState)

    const presetTakeProfitPrice = useRecoilValue(OPPresetTakeProfitPriceState)
    const presetStopLossPrice = useRecoilValue(OPPresetStopLossPriceState)

    const exeTakeProfitPrice = useRecoilValue(OPExecTakeProfitPriceState)
    const exeStopLossPrice = useRecoilValue(OPExecStopLossPriceState)
    const presetTppTriType = useRecoilValue(OPPresetTriggerTakeProfitTypeState)
    const presetSlpTriType = useRecoilValue(OPPresetTriggerStopLossTypeState)
    const inputAmount = useRecoilValue(QOAmountState)

    const { marginMode, fixedShortLeverage, fixedLongLeverage, crossMarginLeverage } =
        useRecoilValue(MixContractUserAccountModeSelector)
    const inputAmountPercent = useRecoilValue(QOPercentageState)
    //市价快速下单
    const quickOrderDelegateCountForBuy = useRecoilValue(
        QuickRealAmountSelector({
            direction: OrderTradeDirectionEnum.Buy
        })
    )
    const quickOrderDelegateCountForSell = useRecoilValue(
        QuickRealAmountSelector({
            direction: OrderTradeDirectionEnum.Sell
        })
    )

    function getTempMarketQuickOrderPlacerParam(direction: OrderTradeDirectionEnum) {
        const tempDelegateCount =
            direction === OrderTradeDirectionEnum.Buy
                ? quickOrderDelegateCountForBuy
                : quickOrderDelegateCountForSell
        return {
            businessLine,
            isReducePosition,
            onlyLow: isReducePosition,
            symbolId: contractConfig.symbolId,
            symbolType: contractConfig.symbolType,
            direction,
            action,
            type,
            priceType: OrderPriceTypeEnum.MARKET,
            delegateCount: tempDelegateCount,
            price,
            triggerPrice,
            triggerType,
            targetPrice,
            tokenId,
            surplusTriggerType,
            leverage:
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
            marginMode: marginMode,
            orderType: OrderPriceTypeEnum.MARKET,
            inputAmount,
            inputAmountPercent,
            tradeUnit,
            presetTakeProfitPrice,
            presetStopLossPrice,
            presetTppTriType,
            presetSlpTriType,
            exeTakeProfitPrice,
            exeStopLossPrice
        }
    }

    // 快速下单场景的参数组装
    function quickOrderParamGetter(direction: OrderTradeDirectionEnum) {
        const tempDelegateCount =
            direction === OrderTradeDirectionEnum.Buy
                ? quickOrderDelegateCountForBuy
                : quickOrderDelegateCountForSell
        if (!isValidInput(tempDelegateCount)) {
            message.error(t('error_text.margin_count_not_enough', '委托数量不足'))
            return
        }

        const calcDelegateType = () => {
            let delegateType: number
            // 单向持仓相关逻辑
            if (positionModeSet === PositionModeEnum.ONE_WAY) {
                // 买入 21  卖出 22
                delegateType =
                    direction === OrderTradeDirectionEnum.Buy
                        ? MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
                        : MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE
                return delegateType
            }
            delegateType =
                direction === OrderTradeDirectionEnum.Buy
                    ? MixContractDelegateType.OPEN_LONG
                    : MixContractDelegateType.OPEN_SHORT
            return delegateType
        }

        const oneWayItem = () => {
            // 单向持仓相关逻辑
            if (positionModeSet === PositionModeEnum.ONE_WAY) {
                return {
                    cancelOrder: !!userPreferenceSetting.quantityIncludesDelegated,
                    onlyLow: isReducePosition,
                    isReducePosition: isReducePosition
                }
            }
            return {}
        }

        // businessLine,
        // isReducePosition,
        // onlyLow: isReducePosition,
        // symbolId: contractConfig.symbolId,
        // symbolType: contractConfig.symbolType,
        // direction,
        // action,
        // type,
        // priceType,
        // delegateCount:
        //     direction === OrderTradeDirectionEnum.Buy
        //         ? delegateCountForBuy
        //         : delegateCountForSell,
        // price,
        // triggerPrice,
        // triggerType,
        // targetPrice,
        // tokenId,
        // surplusTriggerType,
        // leverage:
        //     marginMode === MixContractMarginMode.FIXED_MARGIN
        //         ? direction === OrderTradeDirectionEnum.Buy
        //             ? fixedLongLeverage
        //             : fixedShortLeverage
        //         : crossMarginLeverage,
        // marginMode: marginMode,
        // tif,
        // orderType: getRealOrderType(type, priceType),
        // inputAmount,
        // inputAmountPercent,
        // tradeUnit,
        // presetTakeProfitPrice,
        // presetStopLossPrice,
        // presetTppTriType,
        // presetSlpTriType,
        // exeTakeProfitPrice,
        // exeStopLossPrice

        return {
            tokenId,
            symbolId: contractConfig.symbolId,
            businessLine,
            secondBusinessLine: 'N/A',
            businessSource: BusinessSource.MARKET,
            enterPointSource: EnterPointSource.WEB,
            delegateCount: tempDelegateCount,
            orderType: OrderPriceTypeEnum.MARKET,
            delegateType: calcDelegateType(),
            pricedSymbol: contractConfig.pricedSymbol,
            timeInForceValue: MixContractTimeInForceEnum.GTC,
            tradeUnit,
            ...oneWayItem()
        }
    }
    return [getTempMarketQuickOrderPlacerParam, quickOrderParamGetter]
}
/**
 * 限价快速下单 返回一个参数组装器
 */
export function useLimitQuickOrderPlacerParamGetter(): [
    TempOrderPlacerParamGetter,
    LimitQuickOrderParamGetter
] {
    const positionModeSet = useRecoilValue(UserPreferenceSettingState).positionModeSet // 单双向持仓模式
    const isReducePosition = useRecoilValue(OPOnlyReducePositionPresetState) // 只减仓
    const businessLine = useRecoilValue(CurrentBusinessLineState)
    const contractConfig = useRecoilValue(MixContractInfoState)
    const tokenId = useRecoilValue(CurrentMarginSelector)

    const action = useRecoilValue(OPActionState)
    const type = useRecoilValue(OPTypeState)
    const t = useMyIntl()
    const triggerPrice = useRecoilValue(OPTriggerPriceState)
    const targetPrice = useRecoilValue(OPExecutePriceState)
    const triggerType = useRecoilValue(OPTriggerTypeState)
    const surplusTriggerType = useRecoilValue(OPSurplusTypeState)

    const presetTakeProfitPrice = useRecoilValue(OPPresetTakeProfitPriceState)
    const presetStopLossPrice = useRecoilValue(OPPresetStopLossPriceState)

    const exeTakeProfitPrice = useRecoilValue(OPExecTakeProfitPriceState)
    const exeStopLossPrice = useRecoilValue(OPExecStopLossPriceState)
    const presetTppTriType = useRecoilValue(OPPresetTriggerTakeProfitTypeState)
    const presetSlpTriType = useRecoilValue(OPPresetTriggerStopLossTypeState)

    const { marginMode, fixedShortLeverage, fixedLongLeverage, crossMarginLeverage } =
        useRecoilValue(MixContractUserAccountModeSelector)
    const inputAmount = useRecoilValue(LimitOPAmountState)
    const inputAmountPercent = useRecoilValue(LimitQOPercentageState)

    //限价快速下单
    const quickOrderDelegateCountForBuy = useRecoilValue(
        LimitQuickRealAmountSelector({
            direction: OrderTradeDirectionEnum.Buy
        })
    )
    const quickOrderDelegateCountForSell = useRecoilValue(
        LimitQuickRealAmountSelector({
            direction: OrderTradeDirectionEnum.Sell
        })
    )

    const tif = useRecoilValue(OPTimeInForceState)
    const userPreferenceSetting = useRecoilValue(UserPreferenceSettingState)
    const tradeUnit = useRecoilValue(UserPreferenceSettingState).contractUnitSet
    const price = useRecoilValue(LimitOPPriceState)

    function getTempLimitQuickOrderPlacerParam(direction: OrderTradeDirectionEnum) {
        return {
            businessLine,
            isReducePosition,
            onlyLow: isReducePosition,
            symbolId: contractConfig.symbolId,
            symbolType: contractConfig.symbolType,
            direction,
            action,
            type,
            priceType: OrderPriceTypeEnum.LIMIT,
            delegateCount:
                direction === OrderTradeDirectionEnum.Buy
                    ? quickOrderDelegateCountForBuy
                    : quickOrderDelegateCountForSell,
            price,
            triggerPrice,
            triggerType,
            targetPrice,
            tokenId,
            surplusTriggerType,
            leverage:
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
            marginMode: marginMode,
            tif,
            orderType: OrderPriceTypeEnum.LIMIT,
            inputAmount,
            inputAmountPercent,
            tradeUnit,
            presetTakeProfitPrice,
            presetStopLossPrice,
            presetTppTriType,
            presetSlpTriType,
            exeTakeProfitPrice,
            exeStopLossPrice
        }
    }

    // 快速下单场景的参数组装
    function limitQuickOrderParamGetter(direction: OrderTradeDirectionEnum) {
        const tempDelegateCount =
            direction === OrderTradeDirectionEnum.Buy
                ? quickOrderDelegateCountForBuy
                : quickOrderDelegateCountForSell
        if (!isValidInput(tempDelegateCount)) {
            message.error(t('error_text.margin_count_not_enough', '委托数量不足'))
            return
        }

        const calcDelegateType = () => {
            let delegateType: number
            // 单向持仓相关逻辑
            if (positionModeSet === PositionModeEnum.ONE_WAY) {
                // 买入 21  卖出 22
                delegateType =
                    direction === OrderTradeDirectionEnum.Buy
                        ? MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
                        : MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE
                return delegateType
            }
            delegateType =
                direction === OrderTradeDirectionEnum.Buy
                    ? MixContractDelegateType.OPEN_LONG
                    : MixContractDelegateType.OPEN_SHORT
            return delegateType
        }

        const oneWayItem = () => {
            // 单向持仓相关逻辑
            if (positionModeSet === PositionModeEnum.ONE_WAY) {
                return {
                    cancelOrder: !!userPreferenceSetting.quantityIncludesDelegated,
                    onlyLow: isReducePosition,
                    isReducePosition: isReducePosition
                }
            }
            return {}
        }

        return {
            timeInForceValue: tif,
            delegatePrice: price,
            tokenId,
            symbolId: contractConfig.symbolId,
            businessLine,
            secondBusinessLine: 'N/A',
            businessSource: BusinessSource.NORMAL,
            enterPointSource: EnterPointSource.WEB,
            delegateCount: tempDelegateCount,
            orderType: OrderPriceTypeEnum.LIMIT,
            delegateType: calcDelegateType(),
            pricedSymbol: contractConfig.pricedSymbol,
            tradeUnit,
            ...oneWayItem()
        }
    }

    return [getTempLimitQuickOrderPlacerParam, limitQuickOrderParamGetter]
}

export function createVirtualPositionFromOrder(
    order: TempOrderPlacerParam,
    holdSide: MixContractHoldSide,
    marginMode: MixContractMarginMode
): MixContractPositionPushInfo {
    const { delegateCount, symbolId, tokenId } = order
    return {
        id: '',
        achievedProfits: '0',
        autoAppendMargin: 0,
        available: delegateCount,
        averageOpenPrice: undefined,
        businessLine: undefined,
        holdMode: undefined,
        holdSide,
        key: '',
        leverage: 0,
        locked: '0',
        margin: '0',
        marginMode,
        openDelegateCount: '0',
        symbolId,
        tokenId,
        total: '0',
        baseSymbol: ''
    }
}

//
//
//
//
// ================================================================================================ 追踪委托   移动 止盈 止损
// 触发价格
export const TrackTriggerPrice = atom<string>({
    key: 'op-track-trigger-price',
    default: ''
})

// 回调 % input
export const TrackCallBackState = atom<string>({
    key: 'op-use-callback',
    default: ''
})
// 回调 % 滑块
export const TrackCallBackSliderState = atom<number>({
    key: 'op-use-callback-slider',
    default: 0
})

// 数量
export const TrackTokenNum = atom<string>({
    key: 'op-track-token-num',
    default: ''
})

/**
 * 预设止盈 %
 */
export const TrackTakeProfitState = atom<string>({
    key: 'op-track-take-profit-state',
    default: ''
})

/**
 * 预设止损 %
 */
export const TrackStopLossState = atom<string>({
    key: 'op-track-stop-loss-state',
    default: ''
})

// 追踪委托 开仓   copy 自  useOrderPlacerParamGetter

// 返回类型    弹窗前用
export interface TempTrackOrderPlacerParam {
    symbolId: string
    direction: OrderTradeDirectionEnum
    type: OrderTypeEnum
    action: OrderActionEnum
    triggerType: OrderTriggerTypeEnum

    tokenId: string
    triggerPrice: string
    callBackNum: string | number
    tokenNum: string
    usePreset: boolean
    takeProfit: string
    stopLoss: string
    onlyLow?: boolean
    isReducePosition?: boolean
    leverage: number
    [key: string]: any
}

// 弹窗后用  发请求数据
export interface OrderTrackPlacerParam {
    tokenId: string
    symbolId: string

    businessLine: number
    secondBusinessLine: string
    businessSource: number
    enterPointSource: number
    delegateCount: string
    delegateType: number // ??    //委托类型 1:开多,2:开空,3:平多,4:平空 止盈止损时只有平多，平空
    pricedSymbol: string
    planType: number
    triggerPrice: string
    triggerType: number
    rangeRate: string | number
    presetTakeProfitPriceRatio: string
    presetStopLossPriceRatio: string
    [key: string]: any
}
export function useTrackOrderPlacerParamGetter() {
    // const tls = useMyIntl()
    const positionModeSet = useRecoilValue(UserPreferenceSettingState).positionModeSet // 单双向持仓模式
    const isReducePosition = useRecoilValue(OPOnlyReducePositionPresetState) // 只减仓

    const userPreferenceSetting = useRecoilValue(UserPreferenceSettingState)

    const businessLine = useRecoilValue(CurrentBusinessLineState)
    const contractConfig = useRecoilValue(MixContractInfoState)
    const tokenId = useRecoilValue(CurrentMarginSelector)
    const type = useRecoilValue(OPTypeState)
    const action = useRecoilValue(OPActionState)
    const triggerType = useRecoilValue(OPTriggerTypeState)

    // 触发价格
    const triggerPrice = useRecoilValue(TrackTriggerPrice)
    // 回调 % input
    const callBackNum = useRecoilValue(TrackCallBackState)
    const callBackNumSlider = useRecoilValue(TrackCallBackSliderState)
    // 数量
    const tokenNum = useRecoilValue(TrackTokenNum)
    // 是否 止盈止损
    const usePreset = useRecoilValue(OPUsePresetState)
    // 止盈 %
    const takeProfit = useRecoilValue(TrackTakeProfitState)
    // 止损 %
    const stopLoss = useRecoilValue(TrackStopLossState)
    // 杠杆
    const { marginMode, fixedShortLeverage, fixedLongLeverage, crossMarginLeverage } =
        useRecoilValue(MixContractUserAccountModeSelector)
    const tradeUnit = useRecoilValue(UserPreferenceSettingState).contractUnitSet
    function getTrackTempParam(direction: OrderTradeDirectionEnum): TempTrackOrderPlacerParam {
        return {
            symbolId: contractConfig.symbolId,
            direction,
            type,
            action,
            triggerType,
            tokenId,

            triggerPrice,
            callBackNum: callBackNumSlider || callBackNum,
            tokenNum,
            usePreset,
            takeProfit,
            stopLoss,
            onlyLow: isReducePosition,
            isReducePosition,
            leverage:
                marginMode === MixContractMarginMode.FIXED_MARGIN
                    ? direction === OrderTradeDirectionEnum.Buy
                        ? fixedLongLeverage
                        : fixedShortLeverage
                    : crossMarginLeverage,
            tradeUnit,
            marginMode
        }
    }

    function realTrackParamGetter(direction: OrderTradeDirectionEnum) {
        let result: Partial<OrderTrackPlacerParam> = {
            tokenId,
            symbolId: contractConfig.symbolId,
            businessLine, // U本位还是币本位
            secondBusinessLine: 'N/A',
            // businessSource: BusinessSource.MARKET,  // 非必传 后台自动处理
            enterPointSource: EnterPointSource.WEB,

            delegateCount: tokenNum,
            delegateType: getRealDelegateType(action, direction),
            // 委托类型 1:开多,  2:开空,   3:平多,   4:平空 止盈止损时只有平多，平空

            pricedSymbol: contractConfig.pricedSymbol,
            planType: 6, //action === OrderActionEnum.CLOSE ? 3 : 6,
            //委托类型 0-普通计划 1-止盈计划 2-止损计划 3-移动止盈止损 4-仓位止盈 5-仓位止损 6-追踪委托  // ??

            triggerPrice,
            triggerType, //触发类型 1: 成交价格,   2: 合理标记价格
            rangeRate: callBackNumSlider || callBackNum, // /回调幅度
            presetTakeProfitPriceRatio: action === OrderActionEnum.OPEN ? takeProfit : '', // 预设止盈 5%
            presetStopLossPriceRatio:
                action === OrderActionEnum.OPEN ? stopLoss && `-${stopLoss}` : '' // 预设止损 10%
            // orderType: priceType,
        }

        // 单向持仓
        if (positionModeSet === PositionModeEnum.ONE_WAY) {
            //  需要把是否包含已委托传入
            result.cancelOrder = !!userPreferenceSetting.quantityIncludesDelegated
            result.delegateType = direction === OrderTradeDirectionEnum.Buy ? 21 : 22
            result.presetTakeProfitPriceRatio = takeProfit || '' // 预设止盈 5%  追踪委托 买入 卖出都有 止盈止损
            result.presetStopLossPriceRatio = stopLoss ? `-${stopLoss}` : '' // 预设止损 10%
            result.onlyLow = result.isReducePosition = isReducePosition
        }
        if (!result.presetTakeProfitPriceRatio) delete result.presetTakeProfitPriceRatio // 如果该值为空则删除
        if (!result.presetStopLossPriceRatio) delete result.presetStopLossPriceRatio

        return result
    }

    return [getTrackTempParam, realTrackParamGetter]
}

/**
 * 是否使用预设止盈止损   这个没用过，和其它开仓共用一个止盈止损状态
 */
// export const OPUseCbPreset = atom<boolean>({
//     key: 'op-use-preset',
//     default: false
// })

// // 设置止盈止损 操作
// export const TrackPresetMakeLossState = selector<boolean>({
//     key: 'op-use-cb-preset-selector',
//     get: ({ get }) => {
//         return get(OPUseCbPreset)
//     },
//     set: ({ set }, value) => {
//         set(OPUseCbPreset, value)
//         if (!value) {
//             set(TrackTakeProfitState, '')
//             set(TrackStopLossState, '')
//         }
//     }
// })

/**
 * 余额不足弹窗状态
 */
export const InsufficientVisibleState = atom<boolean>({
    key: 'insufficient-visible-state',
    default: false
})

// /**
//  * 收益率 收益率/杠杆倍数
//  */
// export const CurrentTPSLProfitRateSelector = selector<string>({
//     key: 'current-tpsl-profit-rate-selector',
//     get: ({ get }) => {
//         return get(CurrentMarginState)
//     }
// })
// /**
//  * 涨跌幅
//  */
// export const CurrentTPSLChangeSelector = selector<string>({
//     key: 'current-tpsl-change-selector',
//     get: ({ get }) => {
//         return get(CurrentMarginState)
//     }
// })
// /**
//  * 盈亏 盈亏金额/（价格*数量)
//  */
// export const CurrentTPSLProfitLossSelector = selector<string>({
//     key: 'current-tpsl-profit-loss-selector',
//     get: ({ get }) => {
//         return get(CurrentMarginState)
//     }
// })
